/**
 * LocalStorage cleanup utility
 * Prunes old analytics data and monitors storage size
 */

const WARNING_THRESHOLD_KB = 300;
const CLEANUP_THRESHOLD_KB = 800; // Lowered from 1000 for more aggressive cleanup
const CRITICAL_THRESHOLD_KB = 500; // Trigger nuclear cleanup earlier

interface TimestampedEvent {
  timestamp: string;
  [key: string]: unknown;
}

function pruneOldEvents(key: string, maxAgeDays: number = 30): number {
  try {
    const data = localStorage.getItem(key);
    if (!data) return 0;

    const events: TimestampedEvent[] = JSON.parse(data);
    if (!Array.isArray(events)) return 0;

    const cutoff = Date.now() - (maxAgeDays * 24 * 60 * 60 * 1000);
    const filtered = events.filter(event => {
      if (!event.timestamp) return true;
      const eventTime = new Date(event.timestamp).getTime();
      return eventTime > cutoff;
    });

    const removed = events.length - filtered.length;
    if (removed > 0) {
      localStorage.setItem(key, JSON.stringify(filtered));
    }
    return removed;
  } catch {
    return 0;
  }
}

function getLocalStorageSizeKB(): number {
  let total = 0;
  for (const key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      total += (localStorage.getItem(key)?.length || 0) * 2;
    }
  }
  return total / 1024;
}

function clearOldScanHistory(): number {
  try {
    const historyKey = 'wordpop-scan-history';
    const data = localStorage.getItem(historyKey);
    if (!data) return 0;

    const history = JSON.parse(data);
    if (!Array.isArray(history)) return 0;

    if (history.length > 5) {
      const trimmed = history.slice(-5);
      localStorage.setItem(historyKey, JSON.stringify(trimmed));
      return history.length - 5;
    }
    return 0;
  } catch {
    return 0;
  }
}

/**
 * Aggressive cleanup for when storage exceeds critical threshold
 * Removes more data to get under limit
 */
function aggressiveCleanup(): number {
  let removed = 0;
  
  // Essential keys to preserve
  const essentialKeys = new Set([
    'wordpop-skip-auth',
    'wordpop-build-hash',
    'wordpop-cloud-migrated',
    'wordpop-pwa-prompt-shown',
    'wordpop-stats-last-check',
    'wordpop-stale-data-cleaned-v1',
    'wordpop-debug-enabled',
    'wordpop-secret-debug',
    'wordpop-migration-complete'
  ]);
  
  // 1. Clear all scan-related data
  for (const key in localStorage) {
    if (key.startsWith('wordpop-scan-') || key.startsWith('wordpop-last-scan')) {
      localStorage.removeItem(key);
      removed++;
    }
  }
  
  // 2. Clear all analytics data entirely (not just prune)
  const analyticsKeys = [
    'wordpop-caption-analytics',
    'wordpop-share-analytics', 
    'wordpop-pwa-analytics',
    'wordpop-performance-analytics'
  ];
  for (const key of analyticsKeys) {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      removed++;
    }
  }
  
  // 3. Clear version history entirely
  const versionKey = 'wordpop-version-history';
  if (localStorage.getItem(versionKey)) {
    localStorage.removeItem(versionKey);
    removed++;
  }
  
  // 4. Clear debug-related keys
  for (const key in localStorage) {
    if (key.startsWith('wordpop-debug-') && !essentialKeys.has(key)) {
      localStorage.removeItem(key);
      removed++;
    }
  }
  
  // 5. Clear old game progress for dates older than 30 days
  const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
  for (const key in localStorage) {
    if (key.startsWith('wordpop-progress-')) {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const progress = JSON.parse(data);
          if (progress.lastUpdated) {
            const lastUpdated = new Date(progress.lastUpdated).getTime();
            if (lastUpdated < thirtyDaysAgo) {
              localStorage.removeItem(key);
              removed++;
            }
          }
        }
      } catch { /* ignore */ }
    }
  }
  
  return removed;
}

/**
 * Nuclear cleanup - last resort when still over limit
 * Clears almost everything except critical auth/game state
 */
function nuclearCleanup(): number {
  let removed = 0;
  
  const criticalKeys = new Set([
    'wordpop-skip-auth',
    'wordpop-build-hash',
    'wordpop-cloud-migrated',
    'wordpop-stats-last-check',
    'wordpop-stale-data-cleaned-v1',
    'wordpop-secret-debug',
    'wordpop-pwa-prompt-shown',
    'wordpop-migration-complete'
  ]);
  
  // Keep only today's progress
  const today = new Date().toISOString().split('T')[0];
  const todayProgressKey = `wordpop-progress-${today}`;
  criticalKeys.add(todayProgressKey);
  
  for (const key in localStorage) {
    if (key.startsWith('wordpop-') && !criticalKeys.has(key)) {
      localStorage.removeItem(key);
      removed++;
    }
  }
  
  console.log(`[Nuclear Cleanup] Removed ${removed} keys, preserved critical keys including ${todayProgressKey}`);
  return removed;
}

export function cleanupOldAnalytics(): {
  eventsRemoved: number;
  sizeBeforeKB: number;
  sizeAfterKB: number;
} {
  const sizeBeforeKB = getLocalStorageSizeKB();
  let eventsRemoved = 0;

  // Prune analytics stores (7 days for more aggressive cleanup)
  const analyticsKeys = [
    'wordpop-caption-analytics',
    'wordpop-share-analytics', 
    'wordpop-pwa-analytics',
    'wordpop-performance-analytics'
  ];

  for (const key of analyticsKeys) {
    eventsRemoved += pruneOldEvents(key, 7);
  }

  // Clear old scan history
  eventsRemoved += clearOldScanHistory();

  // Clear old version history entries (keep last 5)
  try {
    const versionKey = 'wordpop-version-history';
    const data = localStorage.getItem(versionKey);
    if (data) {
      const versions = JSON.parse(data);
      if (Array.isArray(versions) && versions.length > 5) {
        localStorage.setItem(versionKey, JSON.stringify(versions.slice(-5)));
        eventsRemoved += versions.length - 5;
      }
    }
  } catch { /* ignore */ }

  // If over cleanup threshold, run aggressive cleanup
  let sizeAfterKB = getLocalStorageSizeKB();
  if (sizeAfterKB > CLEANUP_THRESHOLD_KB) {
    console.log(`[Storage Cleanup] Size ${sizeAfterKB.toFixed(1)}KB exceeds ${CLEANUP_THRESHOLD_KB}KB, running aggressive cleanup...`);
    eventsRemoved += aggressiveCleanup();
    sizeAfterKB = getLocalStorageSizeKB();
  }
  
  // Nuclear option if still over critical threshold
  if (sizeAfterKB > CRITICAL_THRESHOLD_KB) {
    console.log(`[Storage Cleanup] Still ${sizeAfterKB.toFixed(1)}KB after aggressive cleanup, running nuclear cleanup...`);
    eventsRemoved += nuclearCleanup();
    sizeAfterKB = getLocalStorageSizeKB();
  }

  // Log if significant cleanup occurred
  if (eventsRemoved > 0 || sizeBeforeKB > WARNING_THRESHOLD_KB) {
    const debugEnabled = localStorage.getItem('wordpop-debug-enabled') === 'true';
    if (debugEnabled) {
      console.log(`🧹 LocalStorage cleanup: ${eventsRemoved} old entries removed, ${sizeBeforeKB.toFixed(1)}KB → ${sizeAfterKB.toFixed(1)}KB`);
    }
  }

  return { eventsRemoved, sizeBeforeKB, sizeAfterKB };
}

export function getStorageStatus(): {
  sizeKB: number;
  isWarning: boolean;
  isCritical: boolean;
} {
  const sizeKB = getLocalStorageSizeKB();
  return {
    sizeKB,
    isWarning: sizeKB > WARNING_THRESHOLD_KB,
    isCritical: sizeKB > CLEANUP_THRESHOLD_KB
  };
}
